import axios from "axios";
import {
  boxApiBaseUrl,
  boxUploadBaseUrl,
  BOX_APP_USER_NAME_MAX_LENGTH,
} from "@modules/box/constants";
import type {
  FolderResponse,
  BoxResponse,
  BoxFolder,
  BoxFile,
  PaginatedWrapper,
} from "@modules/box/types";

const getHeaders = (accessToken: string) => {
  return {
    headers: {
      "Authorization": `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  };
};

const uploadFile = async ({
  accessToken,
  formData,
  onUploadProgress,
  isMobile = false,
}: {
  accessToken: string;
  formData: FormData;
  onUploadProgress?: (arg0: ProgressEvent) => void;
  isMobile?: boolean;
}) => {
  // TODO: try uploading the file with a production build. Refer to issue: https://github.com/axios/axios/issues/1321
  let res;
  if (isMobile) {
    res = await fetch(`${boxUploadBaseUrl}/files/content`, {
      headers: {
        ...getHeaders(accessToken).headers,
        "Content-Type": "multipart/form-data",
      },
      method: "POST",
      body: formData,
    });
  } else {
    res = await axios.post(`${boxUploadBaseUrl}/files/content`, formData, {
      headers: {
        ...getHeaders(accessToken).headers,
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
  }
  return res;
};

const fetchFolder = async ({
  accessToken,
  folderId,
}: {
  accessToken?: string;
  folderId: string;
}): Promise<FolderResponse> => {
  if (!accessToken) throw Error("Access Token Required to Fetch Folder");
  const fields =
    "item_collection,name,owned_by,created_at,created_by,modified_at,modified_by,extension,file_version,parent,description";
  const result = (await axios
    .get(`${boxApiBaseUrl}/folders/${folderId}`, {
      ...getHeaders(accessToken),
      params: {
        limit: 1000,
        fields,
      },
    })
    .then((response) => response.data)) as BoxResponse<BoxFolder | BoxFile>;
  const { id, name } = result;
  const { limit, offset, order, total_count, entries } = result.item_collection;
  let additionalFiles: BoxFile[] = [];
  if (total_count > limit) {
    let _limit = limit,
      _offset = 0,
      _total_count = total_count;
    while (_total_count > _limit + _offset) {
      const result = (await axios
        .get(`${boxApiBaseUrl}/folders/${folderId}/items`, {
          ...getHeaders(accessToken),
          params: {
            offset: _offset + _limit,
            limit,
            fields,
          },
        })
        .then((response) => response.data)) as PaginatedWrapper<
        BoxFolder | BoxFile
      >;
      _offset = result.offset;
      _limit = result.limit;
      _total_count = result.total_count;
      additionalFiles = additionalFiles.concat(
        result.entries.filter((e) => e.type === "file") as BoxFile[]
      );
    }
  }
  const files = additionalFiles.concat(
    result.item_collection.entries.filter((e) => e.type === "file") as BoxFile[]
  );
  const folders = result.item_collection.entries.filter(
    (e) => e.type === "folder"
  ) as BoxFolder[];
  return {
    id,
    name,
    files,
    folders,
    entries,
    limit,
    offset,
    order,
    total_count,
  };
};

// TODO revisit this
const deleteFile = async ({
  accessToken,
  file,
}: {
  accessToken: string;
  file: BoxFile;
}) => {
  return await axios.delete(
    `${boxApiBaseUrl}/files/${file.id}`,
    getHeaders(accessToken)
  );
};

const deleteFileById = async ({
  accessToken,
  fileId,
}: {
  accessToken: string;
  fileId: string;
}) => {
  return await axios.delete(
    `${boxApiBaseUrl}/files/${fileId}`,
    getHeaders(accessToken)
  );
};

const renameFile = async ({
  accessToken,
  file,
  name,
}: {
  accessToken: string;
  file: BoxFile;
  name: string;
}) => {
  return await axios.put(
    `${boxApiBaseUrl}/files/${file.id}`,
    { name },
    getHeaders(accessToken)
  );
};

const moveFile = async ({
  accessToken,
  file,
  folderId,
}: {
  accessToken: string;
  file: BoxFile;
  folderId: BoxFolder["id"];
}) => {
  return await axios.put(
    `${boxApiBaseUrl}/files/${file.id}`,
    { parent: { id: folderId } },
    getHeaders(accessToken)
  );
};

const getHouseholdAppUserName = (household: Forme.HouseholdWithClientUsers) => {
  try {
    const primaryClient = household.clients.find((client) => client.isPrimary);
    const primaryClientFullName = [
      primaryClient?.firstName,
      primaryClient?.lastName,
    ].join(" ");
    return `Household (${primaryClientFullName})`.substring(
      0,
      BOX_APP_USER_NAME_MAX_LENGTH
    );
  } catch (err) {
    return "Household";
  }
};

export {
  fetchFolder,
  uploadFile,
  deleteFile,
  deleteFileById,
  renameFile,
  moveFile,
  getHouseholdAppUserName,
};
